// Live updates from the server: WebSocket, falling back to polling /api/live. Carries lineup
// patches, check results, the listener count and day changes. Both streams share the channel:
// lineup patches, likes and freezes of the other stream are dropped here.
import { DEMO } from "./demo/flag";
import { STREAM_BPM, withStream } from "./stream";
import { create } from "zustand";

export type LiveMessage =
  | { type: "hello"; listeners: number; day: number; cursor: number }
  | { type: "listeners"; n: number }
  | {
      type: "patch";
      bpm?: number;
      day: number;
      version: number;
      slot: number;
      entry: import("@eb/shared").LineupEntry;
      entries?: import("@eb/shared").LineupEntry[];
      reason: string;
    }
  | ({ type: "likes" } & NowLikes)
  | {
      type: "check";
      draftId: string;
      status: "approved" | "rejected" | "review";
      loopId: string | null;
      wizardShare: number;
      results: unknown;
    }
  | { type: "day"; day: number }
  | { type: "frozen"; bpm?: number; day: number };

/** Likes of the song playing now towards another play (encores). */
export interface NowLikes {
  bpm?: number;
  day: number;
  slot: number;
  loopId: string;
  likes: number;
  need: number | null;
  play: number;
}

interface LiveState {
  connected: "ws" | "poll" | "off";
  listeners: number | null;
  nowLikes: NowLikes | null;
  set: (p: Partial<Omit<LiveState, "set">>) => void;
}

export const useLive = create<LiveState>((set) => ({
  connected: "off",
  listeners: null,
  nowLikes: null,
  set: (p) => set(p),
}));

type Handler = (m: LiveMessage) => void;
const handlers = new Set<Handler>();
let ws: WebSocket | null = null;
let listening = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let cursor = 0;
let started = false;
let wsFailures = 0;
const clientId = Math.random().toString(36).slice(2);

export function onLive(h: Handler): () => void {
  handlers.add(h);
  return () => handlers.delete(h);
}

function dispatch(m: LiveMessage) {
  if ((m.type === "patch" || m.type === "likes" || m.type === "frozen") && (m.bpm ?? 125) !== STREAM_BPM) return;
  if (m.type === "hello") {
    useLive.getState().set({ listeners: m.listeners });
    cursor = Math.max(cursor, m.cursor);
  } else if (m.type === "listeners") useLive.getState().set({ listeners: m.n });
  else if (m.type === "likes") {
    const { type: _t, ...now } = m;
    useLive.getState().set({ nowLikes: now });
  }
  for (const h of handlers) h(m);
}

/** Tell the server whether this tab is listening (for the listener count). */
export function setListening(on: boolean): void {
  listening = on;
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "listening", on }));
}

function connectWs() {
  if (typeof WebSocket === "undefined" || wsFailures >= 3) return startPolling();
  const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${withStream("/ws")}`;
  const sock = new WebSocket(url);
  ws = sock;
  sock.onopen = () => {
    wsFailures = 0;
    useLive.getState().set({ connected: "ws" });
    stopPolling();
    sock.send(JSON.stringify({ type: "listening", on: listening }));
  };
  sock.onmessage = (e) => {
    try {
      dispatch(JSON.parse(String(e.data)) as LiveMessage);
    } catch {
      /* ignore */
    }
  };
  sock.onclose = () => {
    if (ws === sock) ws = null;
    wsFailures++;
    startPolling();
    setTimeout(connectWs, Math.min(30_000, 2000 * wsFailures));
  };
  sock.onerror = () => sock.close();
}

async function pollOnce() {
  try {
    const r = await fetch(withStream(`/api/live?cursor=${cursor}&client=${clientId}&listening=${listening ? 1 : 0}`), {
      cache: "no-store",
    });
    if (r.ok) {
      const body = (await r.json()) as { cursor: number; messages: LiveMessage[]; listeners: number };
      cursor = body.cursor;
      useLive.getState().set({ listeners: body.listeners });
      for (const m of body.messages) dispatch(m);
    }
  } catch {
    /* offline: try again */
  }
}

function startPolling() {
  if (pollTimer || ws?.readyState === WebSocket.OPEN) return;
  useLive.getState().set({ connected: "poll" });
  const loop = async () => {
    await pollOnce();
    pollTimer = setTimeout(loop, 5000);
  };
  pollTimer = setTimeout(loop, 0);
}

function stopPolling() {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
}

/** Start the live channel once per page (`?nows` forces polling, for tests). */
export function startLive(): void {
  if (started) return;
  started = true;
  // The static demo has no server; its backend pushes messages through `pushLive`.
  if (DEMO) return;
  if (new URLSearchParams(location.search).has("nows")) wsFailures = 99;
  connectWs();
}

/** Deliver a live message from inside the page (the static demo's backend). */
export function pushLive(m: LiveMessage): void {
  dispatch(m);
}
